import { NewCampaignState } from "@/services/new-campaign.service";
import { isOneOf, propsAreNonNullish } from "@/utils/type-guards";
import { isMatching, P } from "ts-pattern";
import { createStepCreator } from "../step-creator";

export type PlanningStepperContext = Pick<
  NewCampaignState,
  | "isSetup"
  | "configurations"
  | "delivery"
  | "planning"
  | "isTrialPeriod"
  | "canSendContactsToIntegration"
  | "canSendLiveNumbersToIntegration"
>;

const stepCreator = createStepCreator<PlanningStepperContext>();

const {
  baseSteps: basePlanningSetupSteps,
  createSteps: createPlanningSetupSteps,
} = stepCreator.createBaseSteps([
  {
    id: "type",
    name: "Kampagnetype",
    getIsValid: (context) => !!context.planning,
  },
  {
    id: "lead-amount",
    name: "Antal leads",
    getIsVisible: (context) => context.planning?.type === "OnDemand",
    getIsValid: (context) =>
      context.planning?.type === "OnDemand" &&
      context.planning.leadAmount !== null,
    getIsLocked: (context) => context.isTrialPeriod,
  },
  {
    id: "triggers",
    name: "Salgstriggers",
    getIsVisible: (context) => context.planning?.type === "Triggered",
    getIsLocked: (context) => context.isTrialPeriod,
  },
  {
    id: "lead-amount-and-period",
    name: "Antal leads & periode",
    getIsVisible: (context) =>
      isOneOf(context.planning?.type, "Scheduled", "Triggered"),
    getIsValid: ({ planning, isSetup }) => {
      if (!planning) return false;
      if (isSetup && !planning.chosenStartDate) return false;
      if (
        planning.type === "Scheduled" &&
        !planning.schedules.some(
          (schedule) => schedule.amount !== null && schedule.amount >= 1,
        )
      )
        return false;
      return true;
    },
    getIsLocked: (context) => context.isTrialPeriod,
  },
  {
    id: "sales-strategy-and-prioritization",
    name: (context) =>
      context.configurations.filter((c) => c.enabled).length > 1
        ? "Salgstrategi & prioritering"
        : "Prioritering",
    getIsVisible: (context) =>
      isOneOf(context.planning?.type, "OnDemand", "Scheduled"),
  },
  {
    id: "contact-information",
    name: "Kontaktinformationer",
    getIsVisible: (context) =>
      isMatching(
        P.intersection(
          {
            delivery: {
              type: P.union("Excel", "Integration"),
            },
            planning: P.nonNullable,
          },
          P.union(
            {
              canSendContactsToIntegration: true,
            },
            {
              canSendLiveNumbersToIntegration: true,
            },
          ),
          P.when(
            (context) =>
              // For OnDemand: only show if isSetup
              context.planning?.type !== "OnDemand" || context.isSetup,
          ),
        ) satisfies P.Pattern<PlanningStepperContext>,
      )(context),
    getIsValid: ({ planning }) =>
      !!planning &&
      propsAreNonNullish(
        planning,
        "deliverContacts",
        "deliverContactsRequirePhone",
        "deliverLiveNumber",
      ),
  },
]);

const { createSteps: createPlanningSteps } = stepCreator.createBaseSteps(
  basePlanningSetupSteps.filter(
    (step) => step.id !== "type" && step.id !== "lead-amount",
  ),
);

export { createPlanningSetupSteps, createPlanningSteps };
